import * as React from 'react';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import moment from 'moment';
import { useState, useRef, useEffect } from 'react';
import {
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import Pagination from '../../Pagination';
import { useAuth } from '../../customContextProviders/AuthContext';
import { AddTicketData, GetAllTicket, GetDeviceById } from '../../../network/TicketManagement';
import FrequentlyAskedQuestionsModal from './FrequentlyAskedQuestionsModal';
import SmartLockApiCallerInstance from '../../../utils/SmartLockApiCallerInstance';
import { SMART_LOCK_API_ENDPOINTS } from '../../../URLConstants';
import DisplayHelpCenterImageModal from './DisplayHelpCenterImageModal';

const useStyles = makeStyles((theme) => ({
  textStyle: {
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '18px',
    textAlign: 'left',
    color: '#171D40',
    textWrap: 'nowrap'
  },
  menuItem: {
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px'
    },
    minHeight: '20px',
    borderRadius: '8px',
    marginInline: theme.spacing(1)
  }
}));

export default function HelpCenterDrawer({ open, setOpen, alertMetas, setAlertMetas }) {
  const { sessionUserData } = useAuth();

  const role = sessionUserData?.user?.userRole?.roleName;

  const [fileName, setFileName] = useState(null);
  const fileInputRef = useRef(null);
  const [errors, setErrors] = useState(false);
  const [isFaqModal, setIsFaqModal] = useState(false);
  const [isDisplayImageModal, setIsDisplayImageModal] = useState(false);
  const [displayImage, setDisplayImage] = useState(null);
  const [ticketStep, setTicketStep] = useState('1');
  const classes = useStyles();

  const [ticketData, setTicketData] = useState([]);
  const [page, setPage] = useState(0);
  const [dataPerPage, setDataPerPage] = useState(10);
  const [filterBy, setFilterBy] = useState('');
  const [loader, setLoader] = useState(false);
  const [deviceIds, setDeviceIds] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [selectedTicketData, setSelectedTicketData] = useState(null);
  const [helpCenterData, setHelpCenterData] = useState({
    content: '',
    image: [],
    title: '',
    DeviceIds: ''
  });

  const handleDrawerClose = () => {
    setOpen(false);
    setTicketStep('1');
  };

  const handleImageChange = (e) => {
    const files = e?.target?.files ? Array.from(e.target.files) : [];
    if (files.length > 0) {
      setHelpCenterData((prev) => ({
        ...prev,
        image: [...(prev.image || []), ...files]
      }));
    }
  };

  const handleDelete = (fileName) => {
    setHelpCenterData((prev) => ({
      ...prev,
      image: prev?.image?.filter((file) => file?.name !== fileName)
    }));
  };

  const createHelpTicket = async (e) => {
    e.preventDefault();
    if (helpCenterData?.title?.length === 0) {
      setErrors(true);
    } else {
      setErrors(false);
      const formData = new FormData();
      formData.append('name', sessionUserData.user.name);
      formData.append('title', helpCenterData.title);
      formData.append('content', helpCenterData.content);
      formData.append('email', sessionUserData.user.email);
      formData.append('severity', 'high');
      formData.append('DeviceIds', helpCenterData?.DeviceIds);
      if (helpCenterData?.image) {
        helpCenterData?.image?.forEach((item) => {
          formData?.append('images', item);
        });
      }

      try {
        const res = await AddTicketData(formData);
        if (res) {
          setOpen(false);
          setAlertMetas({
            ...alertMetas,
            severity: 'success',
            snackBarOpened: true,
            msg: 'Ticket submitted successfully !!'
          });
          setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
          resetForm();
        }
      } catch (error) {
        setAlertMetas({
          ...alertMetas,
          severity: 'error',
          snackBarOpened: true,
          msg: error?.response?.data?.message
        });
        setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
        resetForm();
      }
    }
  };

  const getTickets = async (page, dataPerPage) => {
    setLoader(true);
    try {
      const res = await GetAllTicket(page + 1, dataPerPage);
      if (res) {
        setLoader(false);
        setTicketData(res?.data?.data || []);
        setPageCount(res?.data?.total);
      }
    } catch (error) {
      console.log(error, 'error');
    }
  };

  const getDevices = async () => {
    try {
      const res = await GetDeviceById();
      if (res) {
        setDeviceIds(res?.data?.data);
      }
    } catch (error) {
      console.log(error, 'err');
    }
  };

  const handlePageChange = (e, newPage) => {
    setPage(newPage);
  };

  const handleDataPerPage = (e) => {
    setDataPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setHelpCenterData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const resetForm = () => {
    setHelpCenterData({
      content: '',
      image: null,
      title: '',
      DeviceIds: null
    });
    setFileName(null);
    setErrors({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    getTickets(page, dataPerPage);
  }, [page, dataPerPage]);

  useEffect(() => {
    if (role.toUpperCase() !== 'SUPERADMIN') {
      getDevices();
    }
  }, []);

  return (
    <>
      <Drawer
        open={open}
        onClose={handleDrawerClose}
        anchor="right"
        data-testid="help-center-drawer"
        sx={{
          '& .MuiDrawer-paper': {
            borderRadius: '20px 0 0 20px',
            overflow: 'hidden'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: '20px',
            width: '300px',
            height: '100%'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            {ticketStep === 3 ? (
              <IconButton>
                <ArrowBackIosNewOutlinedIcon />
              </IconButton>
            ) : (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setIsFaqModal(true);
                }}
              >
                Open FAQ
              </Button>
            )}

            {ticketStep === '1' ? (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setTicketStep('2');
                }}
              >
                Previous Tickets
              </Button>
            ) : (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setTicketStep('1');
                }}
              >
                Create Ticket
              </Button>
            )}
          </Box>

          {ticketStep === '1' && (
            <>
              <Typography
                sx={{
                  fontSize: { xs: 16, sm: 18 },
                  fontWeight: 900,
                  lineHeight: '24px',
                  mt: 3
                }}
              >
                Create New Ticket:
              </Typography>

              <form
                onSubmit={createHelpTicket}
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ overflow: 'auto' }}>
                  <FormControl data-testid="create-help-ticket" fullWidth>
                    <Typography sx={{ fontSize: '12px', mb: 1, mt: 2 }}>Device ID *</Typography>
                    <FormControl
                      data-testid="device-select"
                      fullWidth
                      sx={{ mb: 2, borderRadius: '10px' }}
                      error={errors && helpCenterData?.DeviceIds?.length === 0}
                    >
                      <Select
                        sx={{ mb: 2, borderRadius: '10px', fontSize: '12px' }}
                        value={helpCenterData.DeviceIds}
                        name="DeviceIds"
                        onChange={handleInputChange}
                        IconComponent={ExpandMoreIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 150,
                              overflowY: 'auto'
                            }
                          }
                        }}
                      >
                        {deviceIds?.map((device, index) => (
                          <MenuItem
                            key={device.id || index}
                            value={device.id}
                            className={classes.menuItem}
                          >
                            {device.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText sx={{ mt: -1.5 }}>
                        {errors && helpCenterData?.DeviceIds?.length === 0 ? 'Required' : ''}
                      </FormHelperText>
                    </FormControl>

                    <Typography sx={{ fontSize: '12px', mb: 1 }}>Subject *</Typography>
                    <TextField
                      data-testid="help-center-subject-input"
                      fullWidth
                      sx={{
                        mb: 2,
                        borderRadius: '10px',
                        '& .MuiInputBase-input': {
                          fontSize: '12px'
                        }
                      }}
                      type="text"
                      value={helpCenterData.title}
                      onChange={handleInputChange}
                      name="title"
                      error={errors && helpCenterData?.title?.length === 0}
                      helperText={errors && helpCenterData?.title?.length === 0 ? 'Required' : ''}
                    />
                    <Typography sx={{ fontSize: '12px', mb: 1 }}>Reason *</Typography>
                    <TextField
                      data-testid="help-center-reason-input"
                      type="text"
                      multiline
                      rows={4}
                      value={helpCenterData.content}
                      onChange={handleInputChange}
                      name="content"
                      error={errors && helpCenterData?.content?.length === 0}
                      helperText={errors && helpCenterData?.content?.length === 0 ? 'Required' : ''}
                      sx={{
                        borderRadius: '10px',
                        '& .MuiInputBase-input': {
                          fontSize: '12px'
                        }
                      }}
                    />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography sx={{ fontSize: '12px', mt: 2, mb: 1 }}>
                        Attach photos/videos (upto 5)
                      </Typography>

                      <input
                        data-testid="help-center-file-input"
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        ref={fileInputRef}
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                      />

                      <Button
                        variant="outlined"
                        sx={{
                          fontSize: '12px',
                          border: '2px dashed #B0B9C7',
                          color: '#5D5D5D',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                        onClick={handleClick}
                      >
                        <AddCircleOutlineIcon sx={{ fontSize: 40 }} />
                      </Button>

                      {helpCenterData?.image?.length > 0 && (
                        <Box
                          sx={{
                            display: 'flex',
                            overflowX: 'auto',
                            whiteSpace: 'nowrap',
                            gap: 1,
                            mt: 2,
                            p: 1
                          }}
                        >
                          {helpCenterData?.image?.map((image, index) => (
                            <Box
                              key={index}
                              sx={{
                                position: 'relative',
                                width: '50px',
                                height: '50px',
                                flexShrink: 0
                              }}
                            >
                              <img
                                src={URL.createObjectURL(image)}
                                alt={`Selected ${index + 1}`}
                                style={{
                                  width: '50px',
                                  height: '50px',
                                  objectFit: 'cover',
                                  borderRadius: '4px'
                                }}
                              />
                              <IconButton
                                data-testid={`delete-image-${index}`}
                                sx={{
                                  position: 'absolute',
                                  top: '-5px',
                                  right: '-5px',
                                  backgroundColor: 'rgba(0,0,0,0.6)',
                                  color: 'white',
                                  fontSize: '12px',
                                  padding: '2px',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.8)'
                                  }
                                }}
                                onClick={() => handleDelete(image.name)}
                              >
                                <CloseOutlinedIcon sx={{ fontSize: '12px' }} />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </FormControl>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'end',
                    marginTop: 'auto',
                    gap: 2
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={handleDrawerClose}
                  >
                    Close
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    type="submit"
                    sx={{ boxShadow: 'none' }}
                  >
                    Submit
                  </Button>
                </Box>
              </form>
            </>
          )}

          {ticketStep === '2' && (
            <Box
              data-testid="previous-ticket-block"
              sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 900,
                  lineHeight: '24px',
                  mt: 3
                }}
              >
                Previous Tickets
              </Typography>
              <Box sx={{ overflow: 'auto', mt: 3, height: '100%', mb: 3 }}>
                {ticketData?.map((item) => (
                  <Box
                    data-testid="block-1"
                    key={item.id}
                    sx={{ p: 1, borderBottom: '1px solid #EBEBEB' }}
                    onClick={() => {
                      setSelectedTicketData(item);
                      setTicketStep('3');
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography className={classes.textStyle}>Ticket ID : {item.id}</Typography>
                      <Typography className={classes.textStyle}>
                        Date : {moment(item.createdAt, moment.ISO_8601).format('DD/MM/YYYY')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography className={classes.textStyle}>
                        Device ID : {item.devices[0]?.name}
                      </Typography>
                      <Typography className={classes.textStyle}>
                        Status : {item.isActive ? 'Active' : 'Pending'}
                      </Typography>
                    </Box>
                    <Typography
                      className={classes.textStyle}
                      style={{
                        marginTop: '15px',
                        textTransform: 'capitalize',
                        fontWeight: '700',
                        textWrap: 'wrap'
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Pagination
                pageNumber={page}
                limit={dataPerPage}
                handleChangePage={handlePageChange}
                handleChangeRowsPerPage={handleDataPerPage}
                pageCount={pageCount}
                sx={{
                  '.MuiPagination-ul': {
                    fontSize: '12px'
                  }
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'end',
                  marginTop: 'auto',
                  gap: 2
                }}
              >
                <Button variant="outlined" color="primary" size="small" onClick={handleDrawerClose}>
                  Close
                </Button>
              </Box>
            </Box>
          )}

          {selectedTicketData !== null && ticketStep === '3' && (
            <>
              <Box data-testid="selected-block" sx={{ p: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '30px',

                    overflowY: 'auto'
                  }}
                >
                  <Typography className={classes.textStyle} style={{ fontWeight: '700' }}>
                    Ticket ID : {selectedTicketData?.id}
                  </Typography>
                  <Typography className={classes.textStyle} style={{ fontWeight: '700' }}>
                    Date :
                    {moment(selectedTicketData?.createdAt, moment.ISO_8601).format('DD/MM/YYYY')}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    className={classes.textStyle}
                    style={{ fontWeight: '700', marginTop: '10px' }}
                  >
                    Device ID : {selectedTicketData?.devices[0]?.name}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    className={classes.textStyle}
                    style={{ marginTop: '20px', fontWeight: '700' }}
                  >
                    Subject
                  </Typography>
                  <Typography
                    className={classes.textStyle}
                    style={{ textTransform: 'capitalize', marginTop: '10px' }}
                  >
                    {selectedTicketData?.title}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    className={classes.textStyle}
                    style={{ marginTop: '20px', fontWeight: '700' }}
                  >
                    Reason
                  </Typography>
                  <Typography
                    className={classes.textStyle}
                    style={{ marginTop: '10px', textWrap: 'wrap' }}
                  >
                    {selectedTicketData?.content}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    className={classes.textStyle}
                    style={{ marginTop: '20px', fontWeight: '700' }}
                  >
                    Photos/Videos
                  </Typography>
                  {selectedTicketData?.images?.length > 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                        gap: 0,
                        mt: 1,
                        p: 1,
                        cursor: 'pointer'
                      }}
                    >
                      {selectedTicketData?.images?.map((image, index) => (
                        <Box
                          key={index}
                          sx={{
                            position: 'relative',
                            width: '50px',
                            height: '50px',
                            flexShrink: 0,
                            margin: '5px'
                          }}
                        >
                          <button
                            type="button"
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              margin: 0,
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setDisplayImage({ image });
                              setIsDisplayImageModal(true);
                            }}
                          >
                            <img
                              src={image}
                              alt={`Selected ${index + 1}`}
                              style={{
                                width: '50px',
                                height: '50px',
                                objectFit: 'cover',
                                borderRadius: '4px'
                              }}
                              onError={(e) => {
                                e.target.onerror = null; // Prevents infinite loop
                                e.target.src = 'https://via.placeholder.com/50'; // Fallback image
                              }}
                            />
                          </button>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: '12px', mt: '10px' }}>
                      No Photos Available
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'end',
                  marginTop: 'auto',
                  gap: 2
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => {
                    setTicketStep('2');
                  }}
                >
                  Back
                </Button>
                <Button variant="outlined" color="primary" size="small" onClick={handleDrawerClose}>
                  Close
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>
      {isFaqModal && (
        <FrequentlyAskedQuestionsModal
          openAssignedModal={isFaqModal}
          handleClose={() => setIsFaqModal(false)}
        />
      )}
      <DisplayHelpCenterImageModal
        openAssignedModal={isDisplayImageModal}
        handleClose={() => setIsDisplayImageModal(false)}
        displayImage={displayImage}
      />
    </>
  );
}
